import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../../environments/urlService';
import { FundPerformance } from '../dto/FundPerformance';
import { Observable } from 'rxjs';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import { PeriodToCopyDto } from '../component/shared/modal-copy-period/dto/PeriodToCopyDto';
import {
  FpCommitmentAllocationDTO,
} from '../component/fund-performances/modal/modal-fund-performance-update';
import { IrrInfoRs } from '../component/fund-performances/pojo/fp-export-classes';
import { ValueCreationChartsDTO, ValueCreationOptions } from "../dto/value-creation-charts-classes";
import {
  ValueCreationOptionsService,
} from "../../investor-report/component/ir/ir-sub-components/ir-value-creation-charts-wrapper/value-creation-charts-service/value-creation-options.service";
import { map } from "rxjs/operators";
import { IrValueCreationChartsPreData } from "../../investor-report/pojo/ir-common-classes";
import { FundsSelectorDto } from "../dto/FundsSelectorDto";
import { DataForOrgSoiWrapper } from '../../soi/dto/SoIAuxiliary';
import { NotEmptyResponseDTO } from '../../../core/pojo/common-dto';

@Injectable({
  providedIn: 'root',
})
export class FundPerformanceBackendService {
  private readonly url: string;
  private readonly settingsUrl: string;

  constructor(private http: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'fund-performance';
    this.settingsUrl = this.urlService.getFmsUserSettingsUrl() + 'funds';
  }

  getFundPerformance(fundUUID: string, date: Date): Promise<FundPerformance> {
    const period = date.toString();
    return this.http.get<FundPerformance>(this.url, {
      params: { fundUUID, period },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    }).toPromise();
  }

  getAllFundPerformancesByFundUuid(fundUUID: string, isDescending: boolean, spinnerIsActive = false): Observable<FundPerformance[]> {
    return this.http.get<FundPerformance[]>(this.url + '/all', {
      params: { fundUUID, isDescending },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, spinnerIsActive),
      withCredentials: true,
    });
  }

  getAllFundPerformancesByOrgUuid(orgUuid: string, period: string): Observable<FundPerformance[]> {
    return this.http.get<FundPerformance[]>(this.url + '/org/all', {
      params: { orgUuid, period },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getSelectedFunds(orgUuid: string): Observable<FundsSelectorDto> {
    return this.http.get<FundsSelectorDto>(this.settingsUrl + '/selected-funds', {
      params: { orgUuid },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  setSelectedFunds(selectorFundsDto: FundsSelectorDto): Observable<void> {
    return this.http.put<void>(this.settingsUrl + '/selected-funds', selectorFundsDto, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  create(fps: FundPerformance[], fundUUID: string): Observable<void> {
    return this.http.post<void>(this.url, fps, {
      params: {
        fundUUID,
      },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'json',
    });
  }

  update(fundPerformance: FundPerformance, withSpinner = true): Promise<FundPerformance> {
    return this.http
      .put<FundPerformance>(
      this.url, fundPerformance, {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, withSpinner),
        responseType: 'json',
      }).toPromise();
  }

  updateCommitmentAllocation(fpCommitmentAllocationDTO: FpCommitmentAllocationDTO): Observable<FundPerformance> {
    const url = this.url + '/commitment-allocation';
    return this.http
      .put<FundPerformance>(
      url, fpCommitmentAllocationDTO, {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      });
  }

  delete(fundUUID: string, date: Date): Observable<void> {
    const period = date.toString();
    return this.http.delete<void>(this.url, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      params: { fundUUID, period },
      withCredentials: true,
    });
  }

  copy(fundUUID: string, periodToCopy: PeriodToCopyDto): Observable<void> {
    return this.http.post<void>(this.url + '/copy', periodToCopy, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      params: { fundUUID },
      withCredentials: true,
    });
  }

  addNewEmptyPeriod(fundUUID: string, period: string): Promise<FundPerformance> {
    return this.http.post<FundPerformance>(
      this.url + '/period',
      {},
      { context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        params: { fundUUID, period },
        withCredentials: true, responseType: 'json' },
    ).toPromise();
  }

  blockPeriod(fundUUID: string, date: Date, isBlocked: boolean): Observable<FundPerformance> {
    const period = date.toString();
    return this.http.put<FundPerformance>(
      this.url + '/block-period',
      {},
      {
        params: { fundUUID, period, isBlocked },
        withCredentials: true,
      },
    );
  }

  toggleIsBlockedInAllPeriods(fundUUID: string): Promise<void> {
    return this.http.put<void>(
      this.url + '/all/toggle-blocked',
      {},
      {
        params: { fundUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true, responseType: 'json',
      },
    ).toPromise();
  }

  changeVisibility(fundUUID: string, date: Date, isVisible: boolean): Observable<FundPerformance> {
    const period = date.toString();
    return this.http.put<FundPerformance>(this.url + '/visibility', {}, {
      params: { fundUUID, period, isVisible },
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
    });
  }

  calculateIrr(fp: FundPerformance, fundUUID: string): Observable<IrrInfoRs> {
    return this.http.post<IrrInfoRs>(
      this.url + '/investor-contribution/irr', fp,
      {
        params: { fundUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        responseType: 'json',
      });
  }

  getAllVisiblePeriods(fundUUID: string): Observable<Date[]> {
    return this.http.get<Date[]>(this.url + '/periods/visible', {
      params: { fundUUID },
      withCredentials: true,
    });
  }

  getAllVisiblePeriodsForFunds(fundsUUIDs: string[]): Observable<Date[]> {
    return this.http.get<Date[]>(this.url + '/periods-for-funds/visible', {
      params: { fundsUUIDs },
      withCredentials: true,
    });
  }

  getValueCreationCharts(dto: IrValueCreationChartsPreData): Observable<ValueCreationChartsDTO> {
    return this.http.post<ValueCreationChartsDTO>(
      `${this.url}/value-creation-charts`,
      dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true,
      }
    );
  }

  getValueCreationChartsOptions(dto: IrValueCreationChartsPreData, multiplicator = 1): Observable<ValueCreationOptions> {
    return this.http.post<ValueCreationChartsDTO>(
      `${this.url}/value-creation-charts`,
      dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true,
      }
    ).pipe(
      map((data: ValueCreationChartsDTO) => ValueCreationOptionsService.getOptions(data, multiplicator))
    );
  }

  /**
   * Checks if any of the given fund performances are non-empty (e.g. contain cost, fair value, or shares).
   *
   * Used to determine whether to display the PPs block based on selected organization and fund performances.
   *
   * @param dto Contains organization UUID and a list of fund performance UUIDs to check.
   * @returns An observable with a boolean flag indicating if any of the given fund performances are non-empty.
   */
  isFPsNotEmpty(dto: DataForOrgSoiWrapper): Observable<NotEmptyResponseDTO> {
    return this.http.put<NotEmptyResponseDTO>(
      `${this.url}/not-empty-by-org`,
      dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
        withCredentials: true,
      }
    );
  }
}
